import React, { useContext, useEffect } from 'react'
import Layout from 'components/layout'
import SEO from 'components/seo'
import Img from 'gatsby-image'
import { injectIntl, Link } from 'gatsby-plugin-intl'
import { graphql } from 'gatsby'
import { colors, breakpoints } from 'utils/utils'
import styled from 'styled-components'
import Slider from 'components/sliderBlog'
import { TiArrowBack } from 'react-icons/ti'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'

export default injectIntl(props => {

    const state = useContext(GlobalStateContext)
    const dispatch = useContext(GlobalDispatchContext)  
     
    useEffect(() => {
        if(state.otherLanguageSlug !== props.pageContext.otherLanguageSlug) {
            dispatch(state.changeOtherLanguageSlug(props.pageContext.otherLanguageSlug))
        }
    }, [state, dispatch, props.pageContext.otherLanguageSlug])

    const { slider, blog } = props.data;
    const { author, content, title, picture, summary, slug, tags, createdAt, updatedAt, publishDate } = blog;

    return (
        < >
            <Layout>
                <SEO 
                    title={ title }
                    slug={slug}
                    keywords={tags}
                    description={summary ? summary.summary : ''}
                    image={picture && picture.file ? picture.file.url : ""}
                    lang={props.pageContext.language}
                    schemaType="blogpost"
                    author={author.name}
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                    publishDate={publishDate ? publishDate : ''}
                />
                <Container>
                    <LinkBackContent>
                        <LinkBack to={`/${props.intl.locale}/blog`}><TiArrowBack/>
                            {/*<FormattedMessage id="blog.back" locale={props.intl.locale} />*/}
                            {props.intl.locale === 'es' && 'Ir al blog'}
                            {props.intl.locale === 'en' && 'Go to blog'}
                        </LinkBack>
                    </LinkBackContent>
                    <ContentWrapper>
                        <Title>{title}</Title>
                        <AuthorDate>
                            {props.intl.locale === 'es' && 'por '}
                            {props.intl.locale === 'en' && 'by '}
                            <Link to={`/${props.intl.locale}/blog?author=${author.name}`}>{author.name}</Link>
                            {publishDate ? ', ' + new Date(publishDate).toLocaleString(props.intl.locale, { dateStyle: "long" }) : ', ' + new Date(createdAt).toLocaleString(props.intl.locale, { dateStyle: "long" })}
                        </AuthorDate>
                        {picture && picture.file ? 
                            <SetImg fluid={ picture.fluid } objectFit="cover" />
                        :
                            <></>
                        }
                        <Content>
                            <div dangerouslySetInnerHTML={{ __html: content && content.content}}></div>
                        </Content>
                        {slider.edges.length !== 0 &&
                            <Related>
                                {/*<FormattedMessage id="blog.related" />*/}
                                {props.intl.locale === 'es' && 'Entradas relacionadas'}
                                {props.intl.locale === 'en' && 'Related posts'}
                            </Related>
                        }
                        <SliderContent>
                            <Slider data={slider} locale={props.intl.locale}/>
                        </SliderContent>
                    </ContentWrapper>
                </Container>
            </Layout>
        </>
    );

});

const SliderContent = styled.div`
    margin: 0 auto;
    width: 100%;
`

const LinkBackContent = styled.div`
    position: fixed;
    top: 5.5rem;
    left: 1rem;
    z-index: 1;
    background-color: ${colors.quinary};
    ${breakpoints.md} {
        top: 7rem;
    }
`

const LinkBack = styled(Link)`
    background: ${colors.quinary};
    color: #000;
    padding: 1rem;
    text-decoration: none;
    display: flex;
    justify-content:center;
    align-content: center;
    max-width: 10rem;
    border-radius: 50px;
    box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
    font-size: 16px;
    ${breakpoints.md} {
        max-width: unset;
    }
`

const Container = styled.div`
    background-color: ${colors.quinary};
`

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
    padding: 8.5rem 2rem 0 2rem;
    ${breakpoints.md}{
        padding: 10rem 1rem 0 1rem;
    }
    img {
        max-width: 100%;
        height: auto;
    }
`

const Title = styled.h1`
    display: inline-block;
    text-align: center;
`

const SetImg = styled(Img)`
    display: block;
    margin: 0 auto;
    flex-grow: 1;
    width: 100%;
    max-width: 600px;
    box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
`

const AuthorDate = styled.span`
    margin-bottom: 1rem;
    font-size: 14px;

    a {
        text-decoration: none;
        &:link,&:active,&:visited {
            color: ${colors.primary};
        }
    }
`

const Content = styled.div`
    margin: 40px auto;
    max-width: 900px;
    width: 100%;
    span{
        line-height: 2rem;
        word-break: break-all;
    }
    p, div {
        line-height: 2rem;
        
        &>span, &>span>em, &>a>span, &>em>span, & li>span, & h1>span, & h2>span, & h3>span, & h4>span, & strong>span {
            word-break: unset;
        }
    }
    pre {
        word-break: break-all;
    }
    ul {
        list-style-type: disc;
    }
`

const Related = styled.h2`
`

export const query = graphql`
    query ContentfulBlogEntryBySlug($language: String, $slug: String!, $tags: [String], $contentful_id: String) {

        blog : contentfulBlogEntry( slug : { eq : $slug}, node_locale: {eq: $language}) {
            title
            picture {
                file {
                    url
                }
                fluid {
                    ...GatsbyContentfulFluid
                }
            }
            author {
                name
                email
                avatar {
                    fixed(width: 100, height: 100, quality:100) {
                        ...GatsbyContentfulFixed
                    }
                }
            }
            tags
            content {
                content
            }
            summary{
                summary
            }
            slug
            createdAt
            updatedAt
            publishDate
        }

        slider : allContentfulBlogEntry(filter: {tags: {in: $tags}, node_locale: {eq: $language}, slug: {ne: $slug}}) {
            edges {
                node {
                    id
                    slug
                    title
                    picture {
                        file {
                            url
                        }
                    }
                }
            }
        }

        slugs:   allContentfulBlogEntry(filter: {contentful_id: {eq: $contentful_id}}) {
            edges {
                node {
                    id
                    slug
                    node_locale
                }
            }
        }
    }`
    

